import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Card } from "./ui/card";
import { Activity, RefreshCw, CheckCircle2, XCircle, ChevronDown, ChevronRight, AlertTriangle } from "lucide-react";

/**
 * Admin-only widget: polls /api/_health every 30s and shows per-check status.
 * Also renders a collapsible tail of /var/log/supervisor/backend.err.log so
 * admins can spot 500s / stack traces from the browser without shelling in.
 */
export default function SystemHealthCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [errData, setErrData] = useState(null);
  const [errLoading, setErrLoading] = useState(false);
  const [errOpen, setErrOpen] = useState(false);
  const [errError, setErrError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/_health");
      setData(data);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadErrors = useCallback(async () => {
    setErrLoading(true);
    setErrError("");
    try {
      const { data } = await api.get("/_health/errors", { params: { lines: 120 } });
      setErrData(data);
    } catch (e) {
      setErrError(formatApiError(e));
    } finally {
      setErrLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 30_000);
    return () => clearInterval(id);
  }, [load]);

  // Auto-refresh error tail whenever it's expanded — but not while collapsed
  useEffect(() => {
    if (!errOpen) return;
    loadErrors();
    const id = setInterval(loadErrors, 30_000);
    return () => clearInterval(id);
  }, [errOpen, loadErrors]);

  const status = data?.status;
  const errorLines = errData?.lines || [];
  const errorCount = errorLines.filter((l) => /error|traceback|exception|critical/i.test(l)).length;
  // Backend note (e.g. "log file not found" on production deployments where
  // supervisor writes logs to a different path). Distinguishes "environment
  // doesn't expose logs here" from "logs are empty because backend is quiet".
  const errNote = errData?.note || errData?.error || null;

  return (
    <Card className="bg-white border border-slate-200 rounded-none p-6" data-testid="system-health-card">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Activity className="w-4 h-4 text-[#0D9488]" />
            <div className="font-mono-num text-[10px] text-[#0D9488] uppercase tracking-[0.3em]">// System Health</div>
          </div>
          <h2 className="font-display font-black uppercase tracking-tight text-2xl text-slate-900">Backend Status</h2>
          <p className="text-slate-500 text-xs mt-1">Auto-refreshes every 30s. Runs the same serializers the app uses.</p>
        </div>
        <div className="flex items-center gap-2">
          {status && (
            <span
              className={`px-3 py-1 text-[10px] font-mono-num uppercase tracking-widest border ${
                status === "ok"
                  ? "border-emerald-500/40 text-emerald-400 bg-emerald-500/5"
                  : "border-red-500/40 text-red-400 bg-red-500/5"
              }`}
              data-testid={`system-health-status-${status}`}
            >
              {status === "ok" ? "All systems OK" : "Degraded"}
            </span>
          )}
          <button
            type="button"
            onClick={load}
            disabled={loading}
            title="Refresh now"
            aria-label="Refresh now"
            className="w-9 h-9 flex items-center justify-center border border-slate-200 hover:border-[#0D9488] hover:text-[#0D9488] text-slate-500 transition-colors disabled:opacity-50"
            data-testid="system-health-refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {error && (
        <div className="border border-red-500/40 bg-red-500/5 text-red-400 text-sm p-3 mb-3 font-mono-num" data-testid="system-health-error">
          {error}
        </div>
      )}

      {data && (
        <div className="border border-slate-200 divide-y divide-zinc-900">
          {data.checks.map((c) => (
            <div key={c.name} className="flex items-center justify-between px-4 py-3" data-testid={`system-health-row-${c.name}`}>
              <div className="flex items-center gap-3 min-w-0">
                {c.ok ? (
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                ) : (
                  <XCircle className="w-4 h-4 text-red-400 shrink-0" />
                )}
                <div className="min-w-0">
                  <div className="text-slate-900 font-mono-num text-sm">{c.name}</div>
                  {!c.ok && c.error && (
                    <div className="text-red-400/80 text-[11px] font-mono-num truncate mt-0.5" title={c.error}>
                      {c.error}
                    </div>
                  )}
                  {c.ok && c.detail && (
                    <div className="text-slate-500 text-[11px] font-mono-num truncate">
                      {Object.entries(c.detail).map(([k, v]) => `${k}=${v}`).join("  ·  ")}
                    </div>
                  )}
                </div>
              </div>
              <div className="font-mono-num text-[11px] tabular-nums text-slate-500 shrink-0 ml-3">
                {c.latency_ms} ms
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Backend errors — collapsible log tail */}
      <div className="mt-4 border border-slate-200" data-testid="system-health-errors-block">
        <button
          type="button"
          onClick={() => setErrOpen((v) => !v)}
          className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50/50 transition-colors"
          data-testid="system-health-errors-toggle"
        >
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-4 h-4 text-[#0D9488]" />
            <div className="text-left">
              <div className="font-mono-num text-sm text-slate-900">Backend Error Tail</div>
              <div className="text-[11px] font-mono-num text-slate-500 uppercase tracking-widest">
                {errNote && errorLines.length === 0
                  ? "Not available on this environment"
                  : `Last ${errData?.lines?.length ?? "?"} lines of backend.err.log`}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {errData && (
              <span className={`px-2 py-0.5 text-[10px] font-mono-num uppercase tracking-widest border tabular-nums ${
                errorCount > 0
                  ? "border-red-500/40 text-red-400 bg-red-500/5"
                  : "border-emerald-500/40 text-emerald-400 bg-emerald-500/5"
              }`} data-testid="system-health-errors-count">
                {errorCount} error{errorCount === 1 ? "" : "s"} in tail
              </span>
            )}
            {errOpen ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
          </div>
        </button>

        {errOpen && (
          <div className="border-t border-slate-200 bg-black/40 p-3" data-testid="system-health-errors-panel">
            <div className="flex items-center justify-between mb-2">
              <div className="font-mono-num text-[10px] uppercase tracking-widest text-slate-500">
                {errData?.path || "/var/log/supervisor/backend.err.log"}
              </div>
              <button
                type="button"
                onClick={loadErrors}
                disabled={errLoading}
                className="w-7 h-7 flex items-center justify-center border border-slate-200 hover:border-[#0D9488] hover:text-[#0D9488] text-slate-500 transition-colors disabled:opacity-50"
                data-testid="system-health-errors-refresh"
                title="Refresh error tail"
              >
                <RefreshCw className={`w-3 h-3 ${errLoading ? "animate-spin" : ""}`} />
              </button>
            </div>
            {errError && (
              <div className="border border-red-500/40 bg-red-500/5 text-red-400 text-xs p-2 mb-2 font-mono-num">{errError}</div>
            )}
            {errNote && errorLines.length === 0 && !errLoading && (
              <div
                className="border border-slate-200 bg-slate-50/60 text-slate-600 text-xs p-3 mb-2 font-mono-num leading-relaxed"
                data-testid="system-health-errors-note"
              >
                <div className="text-slate-800 mb-1">// {errNote}</div>
                <div className="text-[11px] text-slate-500">
                  This is expected on production deployments — supervisor log files
                  aren&apos;t available here. The Backend Error Tail works in the
                  preview environment for debugging.
                </div>
              </div>
            )}
            <pre
              className="text-[11px] font-mono-num text-slate-800 leading-relaxed whitespace-pre-wrap break-all max-h-80 overflow-y-auto"
              data-testid="system-health-errors-log"
            >
              {errorLines.length === 0
                ? (errLoading
                    ? "Loading…"
                    : errNote
                      ? ""  // note banner above already explains
                      : "// Backend is quiet — no recent log activity ✓")
                : errorLines.map((l, i) => {
                    const highlight = /\b(error|traceback|exception|critical)\b/i.test(l);
                    return (
                      <span key={i} className={highlight ? "text-red-400 block" : "block"}>{l}</span>
                    );
                  })}
            </pre>
          </div>
        )}
      </div>

      {data && (
        <div className="mt-3 text-[10px] font-mono-num text-slate-400 uppercase tracking-widest">
          Checked at {new Date(data.checked_at).toLocaleTimeString()}
        </div>
      )}
    </Card>
  );
}
